import { readItems } from "@directus/sdk";
import { getDirectusClient } from "../lib/directus";

class FacturasService {
  /**
   * Obtiene facturas con paginación y filtros optimizados para la tabla
   * @param {Object} options - Opciones de consulta
   * @param {Object} options.filterObj - Filtros a aplicar
   * @param {number} options.page - Página actual
   * @param {number} options.pageSize - Tamaño de página
   * @returns {Promise<Array>} Lista de facturas
   */
  static async getFacturasTableData({ filterObj, page = 1, pageSize = 10 }) {
    try {
      const directus = await getDirectusClient();
      
      const response = await directus.request(
        readItems("facturas", {
          fields: [
            "id",
            "consecutivo",
            "fecha_facturacion",
            "estado",
            "total",
            "cliente.first_name",
            "cliente.last_name",
            "vendedor_factura.first_name",
            "vendedor_factura.last_name",
            "productos.id",
            "productos.cantidad",
            "productos.precio_unitario",
            "productos.producto.grupo.impuesto",
            "productos.seriales.id"
          ],
          filter: filterObj,
          sort: ["-id"],
          limit: pageSize,
          offset: (page - 1) * pageSize,
       
        })
      );

      return response;
    } catch (error) {
      console.error("Error fetching facturas table data:", error);
      throw error;
    }
  }

  /**
   * Obtiene el conteo total de facturas con filtros
   * @param {Object} filterObj - Filtros a aplicar
   * @returns {Promise<number>} Total de facturas
   */
  static async getFacturasCount(filterObj) {
    try {
      const directus = await getDirectusClient();
      
      const countResponse = await directus.request(
        readItems("facturas", {
          aggregate: { count: "id" },
          filter: filterObj,
        })
      );

      return countResponse[0]?.count?.id || 0;
    } catch (error) {
      console.error("Error fetching facturas count:", error);
      throw error;
    }
  }

  /**
   * Obtiene detalles completos de una factura específica para el modal
   * @param {string} facturaId - ID de la factura
   * @returns {Promise<Object>} Detalles completos de la factura
   */
  static async getFacturaDetails(facturaId) {
    try {
      const directus = await getDirectusClient();
      
      const response = await directus.request(
        readItems("facturas", {
          fields: [
            "fecha_facturacion",
            "tipo",
            "estado",
            "cliente.first_name",
            "cliente.last_name",
            "cliente.documento",
            "vendedor_factura.first_name",
            "vendedor_factura.last_name",
            "vendedor_prestamo.first_name",
            "vendedor_prestamo.last_name",
            "vendedor_factura.documento",
            "vendedor_prestamo.documento",
            "productos.nombre",
            "productos.precio_unitario",
            "productos.cantidad",
            "productos.producto.*",
            "productos.producto.grupo.impuesto",
            "user_created.first_name",
            "user_created.last_name",
            "productos.seriales.*",
            "productos.seriales.estado.estado",
            "bodega.nombre",
            "caja.id",
          ],
          filter: {
            id: { _eq: facturaId }
          },
       
        })
      );
      
      return response && response.length > 0 ? response[0] : null;
    } catch (error) {
      console.error("Error fetching factura details:", error);
      throw error;
    }
  }

  /**
   * Obtiene datos de resumen para calcular totales y conteos
   * @param {Object} filterObj - Filtros a aplicar
   * @returns {Promise<Object>} Datos de resumen
   */
  static async getFacturasSummary(filterObj) {
    try {
      const directus = await getDirectusClient();
      
      // Obtenemos las facturas con sus productos para calcular el total correctamente
      const response = await directus.request(
        readItems("facturas", {
          fields: [
            "id",
            "total",
            "productos.cantidad",
            "productos.precio_unitario",
            "productos.producto.grupo.impuesto"
          ],
          sort: ["-fecha_facturacion"],
          filter: filterObj,
          limit: -1 // Get all results
        })
      );

      let totalFacturas = 0;
      let sumaTotal = 0;

      if (response && Array.isArray(response)) {
        totalFacturas = response.length;
        
        response.forEach((factura) => {
          let facturaTotal = 0;
          
          // Si la factura ya tiene total calculado, lo usamos
          if (factura.total && !isNaN(parseFloat(factura.total))) {
            facturaTotal = parseFloat(factura.total);
          } else {
            // Si no tiene total, lo calculamos sumando los productos
            if (factura.productos && Array.isArray(factura.productos)) {
              factura.productos.forEach(producto => {
                const cantidad = parseFloat(producto.cantidad) || 0;
                const precioUnitario = parseFloat(producto.precio_unitario) || 0;
                const impuesto = parseFloat(producto.producto?.grupo?.impuesto) || 0;
                
                // Calcular subtotal del producto
                const subtotalProducto = cantidad * precioUnitario;
                
                // Calcular impuesto del producto
                const impuestoProducto = subtotalProducto * (impuesto / 100);
                
                // Agregar al total de la factura
                facturaTotal += subtotalProducto + impuestoProducto;
              });
            }
          }
          
          sumaTotal += facturaTotal;
        });
      }

      // Para el conteo de seriales, usamos el método optimizado que ya creamos
      // No lo calculamos aquí para evitar la caída
      const totalSeriales = 0; // Se calculará por separado usando getSerialesCountByFacturas

      return {
        totalFacturas,
        sumaTotal: Math.round(sumaTotal * 100) / 100, // Redondear a 2 decimales
        totalSeriales,
      };
    } catch (error) {
      console.error("Error calculating summary data:", error);
      return {
        totalFacturas: 0,
        sumaTotal: 0,
        totalSeriales: 0,
      };
    }
  }

  /**
   * Construye el objeto de filtros basado en los parámetros de búsqueda
   * @param {Object} params - Parámetros de búsqueda
   * @param {string} params.consecutivo - Consecutivo a buscar
   * @param {string} params.vendedor - ID del vendedor
   * @param {Object} params.fecha - Fecha de búsqueda (moment object)
   * @param {string} params.grupo - ID del grupo de productos
   * @param {string} userBodega - ID de la bodega del usuario
   * @returns {Object} Objeto de filtros para Directus
   */
  static buildFilterObject({ consecutivo, vendedor, fecha, grupo }, userBodega) {
    const filterObj = {
      _and: [{ bodega: { _eq: userBodega } }],
    };

    if (consecutivo) {
      filterObj._and.push({
        consecutivo: { _contains: consecutivo },
      });
    }

    if (vendedor) {
      filterObj._and.push({
        vendedor_factura: {
          id: { _eq: vendedor },
        },
      });
    }

    if (fecha) {
      const startDate = fecha.startOf("day").toISOString();
      const endDate = fecha.endOf("day").toISOString();
      filterObj._and.push({
        fecha_facturacion: {
          _between: [startDate, endDate],
        },
      });
    }

    if (grupo) {
      filterObj._and.push({
        productos: {
          producto: {
            grupo: {
              id: { _eq: grupo }
            }
          }
        }
      });
    }

    return filterObj;
  }

  /**
   * Obtiene el conteo de seriales por factura con filtros específicos
   * @param {string} facturaId - ID de la factura
   * @param {string} estadoId - ID del estado (por defecto 2)
   * @returns {Promise<number>} Conteo de seriales únicos
   */
  static async getSerialesCountByFactura(facturaId, estadoId = "2") {
    try {
      const directus = await getDirectusClient();
      
      const response = await directus.request(
        readItems("seriales", {
          aggregate: { countDistinct: "id" },
          filter: {
            _and: [
              {
                estado: {
                  id: { _eq: estadoId }
                }
              },
              {
                produtofactura: {
                  factura: {
                    id: { _eq: facturaId }
                  }
                }
              }
            ]
          }
        })
      );

      return response[0]?.countDistinct?.id || 0;
    } catch (error) {
      console.error("Error fetching seriales count by factura:", error);
      throw error;
    }
  }

  /**
   * Obtiene el conteo total de seriales para múltiples facturas con filtros específicos
   * @param {Array<string>} facturaIds - Array de IDs de facturas
   * @param {string} estadoId - ID del estado (por defecto 2)
   * @param {string} grupoId - ID del grupo de productos (opcional)
   * @returns {Promise<number>} Conteo total de seriales únicos
   */
  static async getSerialesCountByFacturas(facturaIds, estadoId = "2", grupoId = null) {
    try {
      if (!facturaIds || facturaIds.length === 0) {
        return 0;
      }

      const directus = await getDirectusClient();
      
      // Build filter array
      const filterArray = [
        {
          estado: {
            id: { _eq: estadoId }
          }
        },
        {
          produtofactura: {
            factura: {
              id: { _in: facturaIds }
            }
          }
        }
      ];

      // Add grupo filter if provided
      if (grupoId) {
        filterArray.push({
          produtofactura: {
            producto: {
              grupo: {
                id: { _eq: grupoId }
              }
            }
          }
        });
      }
      
      const response = await directus.request(
        readItems("seriales", {
          aggregate: { countDistinct: "id" },
          filter: {
            _and: filterArray
          }
        })
      );

      return response[0]?.countDistinct?.id || 0;
    } catch (error) {
      console.error("Error fetching seriales count by facturas:", error);
      throw error;
    }
  }
}

export default FacturasService;
